/**
 * SMSチャットアプリAPI用 axios
 *
 * @author Tsue Shogo
 * @since 2023-04-19
 */
import axios from 'axios'
import store from '@/store'

export class SMSAxios {
  /**
   * インスタンス
   *
   * @type {AxiosInstance|null}
   */
  static instance = null

  /**
   * axiosインスタンスを返す
   *
   * @param {string}  baseURL
   * @returns {AxiosInstance}
   */
  static getInstance = (baseURL, forceFlg = false) => {
    if (this.instance && !forceFlg) {
      return this.instance
    }

    // instance生成
    const instance = axios.create({
      baseURL,
      timeout: 25000
    })
    store.commit('SMSChatMessage/set', { stateKey: 'loginRetry', data: false }) // NOTE: なぜかtrueで固定されることがあるのでここでfalseにしておく

    // リクエストの修飾
    instance.interceptors.request.use((request) => {
      switch (request.url) {
        case '/admin/auth_login/': {
          // 何もしない
          break
        }
        case '/admin/refresh_token/': {
          // リフレッシュトークンをヘッダに載せる
          const refreshToken = store.getters['SMSChatMessage/get']('refreshToken')
          request.headers.Authorization = `Bearer ${refreshToken}`
          break
        }
        default: {
          // アクセストークンをヘッダに載せる
          const accessToken = store.getters['SMSChatMessage/get']('accessToken')
          request.headers.Authorization = `Bearer ${accessToken}`
        }
      }
      return request
    })

    // レスポンスの修飾
    instance.interceptors.response.use((response) => {
      // アクセストークンとリフレッシュトークンを受け取ったらストアに入れる
      if (response.data.access_token && response.data.refresh_token) {
        store.dispatch('SMSChatMessage/saveTokens', {
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token
        })
      }
      return response
    }, async function (error) {
      const originalRequest = error.config
      const originalResponse = error.response

      // Authorizationでトークンを送信していて401でリトライじゃない場合
      if (
        store.getters['SMSChatMessage/get']('accessToken') &&
        store.getters['SMSChatMessage/get']('refreshToken') &&
        originalRequest.headers?.Authorization &&
        originalResponse.status === 401 &&
        !store.getters['SMSChatMessage/get']('loginRetry')
      ) {
        // リトライフラグオン
        console.log('[smsAxios][response.interceptors][error] リフレッシュ(retry=true)')
        store.commit('SMSChatMessage/set', { stateKey: 'loginRetry', data: true })

        // リフレッシュトークンで更新
        return await instance.post('/admin/refresh_token/').then(() => {
          // 成功
          console.log('[smsAxios][response.interceptors][error] リフレッシュ成功')
          const accessToken = store.getters['SMSChatMessage/get']('accessToken')
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return instance.request(originalRequest)
        }).catch().finally(() => {
          // リトライフラグオフ
          console.log('[smsAxios][response.interceptors][error] リフレッシュ終了(retry=false)')
          store.commit('SMSChatMessage/set', { stateKey: 'loginRetry', data: false })
        })
      }

      return Promise.reject(error)
    })

    this.instance = instance
    return instance
  }
}
